import React, { useEffect, useState } from 'react';
import { useClientPortal } from '../../context/ClientPortalContext';
import { PortalDocumentsService } from '../../services/portal/portalDocumentsService';
import DragDropUpload from '../../components/client/documents/DragDropUpload';
import DocumentCard from '../../components/client/documents/DocumentCard';
import { DocumentIcon, FolderIcon, CheckCircleIcon, ClockIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

const ClientDocumentsPage = () => {
  const { clientId } = useClientPortal();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    if (clientId) loadDocuments();
  }, [clientId]);

  const loadDocuments = async () => {
    setLoading(true);
    const { data } = await PortalDocumentsService.listDocuments(clientId);
    setDocuments(data || []);
    setLoading(false);
  };

  const handleFileUpload = async (file) => {
    if (!file) return;

    setUploading(true);
    const { error } = await PortalDocumentsService.uploadDocument({
      clientId,
      file,
      title: file.name
    });
    setUploading(false);

    if (error) {
      alert('Upload failed. Please try again.');
    } else {
      loadDocuments();
    }
  };

  const handleDownload = async (doc) => {
    const { data: url, error } = await PortalDocumentsService.getDocumentUrl(doc.storage_path);
    if (error || !url) {
      alert('Failed to get download URL');
      return;
    }
    window.open(url, '_blank');
  };

  const handlePreview = async (doc) => {
    // For now, same as download - could implement modal preview later
    handleDownload(doc);
  };

  // Categorize documents
  const categorizedDocs = {
    all: documents,
    required: documents.filter(d => d.status === 'required' || !d.status),
    submitted: documents.filter(d => ['submitted', 'pending', 'under_review'].includes(d.status?.toLowerCase())),
    approved: documents.filter(d => ['approved', 'verified', 'completed'].includes(d.status?.toLowerCase())),
    rejected: documents.filter(d => d.status?.toLowerCase() === 'rejected')
  };

  const categories = [
    { id: 'all', label: 'All Documents', icon: FolderIcon, count: documents.length },
    { id: 'required', label: 'Required', icon: ExclamationCircleIcon, count: categorizedDocs.required.length, color: 'text-blue-600' },
    { id: 'submitted', label: 'Pending Review', icon: ClockIcon, count: categorizedDocs.submitted.length, color: 'text-amber-600' },
    { id: 'approved', label: 'Approved', icon: CheckCircleIcon, count: categorizedDocs.approved.length, color: 'text-emerald-600' }
  ];

  const displayDocs = categorizedDocs[activeCategory] || documents;

  // Calculate progress
  const totalRequired = categorizedDocs.required.length + categorizedDocs.submitted.length + categorizedDocs.approved.length;
  const completed = categorizedDocs.approved.length;
  const progress = totalRequired > 0 ? Math.round((completed / totalRequired) * 100) : 0;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-32 bg-slate-100 rounded-2xl animate-pulse"></div>
        <div className="h-48 bg-slate-100 rounded-2xl animate-pulse"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Progress */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">My Documents</h1>
            <p className="text-indigo-100 mt-1 text-sm">
              Upload and manage your application documents
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-2xl font-bold">{progress}%</p>
              <p className="text-xs text-indigo-200">Complete</p>
            </div>
            <div className="w-20 h-20 relative">
              <svg className="w-20 h-20 transform -rotate-90">
                <circle
                  cx="40"
                  cy="40"
                  r="36"
                  stroke="currentColor"
                  strokeWidth="6"
                  fill="none"
                  className="text-white/20"
                />
                <circle
                  cx="40"
                  cy="40"
                  r="36"
                  stroke="currentColor"
                  strokeWidth="6"
                  fill="none"
                  strokeDasharray={`${2 * Math.PI * 36}`}
                  strokeDashoffset={`${2 * Math.PI * 36 * (1 - progress / 100)}`}
                  className="text-white transition-all duration-700"
                  strokeLinecap="round"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Section */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Upload Documents</h2>
        <DragDropUpload
          onUpload={handleFileUpload}
          uploading={uploading}
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
          maxSize={10 * 1024 * 1024}
          multiple={true}
        />
      </div>

      {/* Categories & Documents */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        {/* Category Tabs */}
        <div className="flex gap-1 p-3 border-b border-slate-100 overflow-x-auto">
          {categories.map(cat => {
            const Icon = cat.icon;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`
                                    flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-colors
                                    ${activeCategory === cat.id
                    ? 'bg-indigo-50 text-indigo-700'
                    : 'text-slate-600 hover:bg-slate-50'}
                                `}
              >
                <Icon className={`h-4 w-4 ${cat.color || ''}`} />
                {cat.label}
                <span className={`
                                    px-1.5 py-0.5 rounded-full text-xs
                                    ${activeCategory === cat.id ? 'bg-indigo-100' : 'bg-slate-100'}
                                `}>
                  {cat.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Document Grid */}
        <div className="p-4">
          {displayDocs.length === 0 ? (
            <div className="text-center py-12">
              <DocumentIcon className="h-12 w-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No documents in this category</p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {displayDocs.map((doc) => (
                <DocumentCard
                  key={doc.id}
                  document={doc}
                  onDownload={handleDownload}
                  onPreview={handlePreview}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientDocumentsPage;
